import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  [
    "inline-flex items-center gap-1.5 whitespace-nowrap font-semibold",
    "[&_svg]:shrink-0 [&_svg]:size-[1.05em]",
  ],
  {
    variants: {
      variant: {
        brand: "bg-brand-500/12 text-brand-700 dark:text-brand-200 ring-1 ring-brand-500/22",
        accent:
          "bg-accent-500/12 text-accent-700 dark:text-accent-200 ring-1 ring-accent-500/22",
        neutral:
          "bg-[var(--canvas-subtle)] text-[var(--content-muted)] ring-1 ring-[var(--hairline)]",
        success:
          "bg-success-500/12 text-success-600 dark:text-success-400 ring-1 ring-success-500/25",
        warning:
          "bg-warning-500/14 text-warning-600 dark:text-warning-400 ring-1 ring-warning-500/25",
        danger: "bg-danger-500/12 text-danger-600 dark:text-danger-400 ring-1 ring-danger-500/25",
        solid: "bg-linear-to-b from-brand-500 to-brand-700 text-white shadow-[0_8px_20px_-8px_rgb(34_113_198/0.85)]",
        outline: "text-[var(--content-muted)] ring-1 ring-[var(--hairline-strong)]",
      },
      size: {
        sm: "h-6 rounded-full px-2.5 text-[0.6875rem]",
        md: "h-7 rounded-full px-3 text-xs",
        lg: "h-8 rounded-full px-3.5 text-[0.8125rem]",
      },
    },
    defaultVariants: { variant: "brand", size: "md" },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant, size }), className)} {...props} />
}

/** Small pulsing dot used for live/stock indicators. */
export function StatusDot({
  tone = "success",
  className,
}: {
  tone?: "success" | "warning" | "danger"
  className?: string
}) {
  const color = {
    success: "bg-success-500",
    warning: "bg-warning-500",
    danger: "bg-danger-500",
  }[tone]

  return (
    <span className={cn("relative flex size-2", className)} aria-hidden>
      <span
        className={cn("absolute inline-flex size-full rounded-full opacity-70", color)}
        style={{ animation: "var(--animate-pulse-ring)" }}
      />
      <span className={cn("relative inline-flex size-2 rounded-full", color)} />
    </span>
  )
}

export { badgeVariants }
